import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { AppSettings } from '../shared/Constants/sharedConstants';
import { CurrentTheme, ICurrentTheme } from '../shared/models/uiapp-config';
import { RestClient } from '../shared/communication-objects/restclient.service';
import { LogService } from './log.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeSettingsService implements OnDestroy {
  public currentTheme$: BehaviorSubject<CurrentTheme> =
    new BehaviorSubject<CurrentTheme>(null);
  public themeEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _destroy$ = new Subject<boolean>();
  private _adaptiveThemeUrl$ = new BehaviorSubject<string>(null);

  themeConfig$ = new BehaviorSubject<ICurrentTheme>(
    AppSettings?.UIAppConfig?.currentTheme,
  );
  window: Window & typeof globalThis;

  adaptiveThemeValue: string; //todo:

  constructor(
    private http: RestClient,
    @Inject(DOCUMENT) private document: Document,
    private logger: LogService,
    private utilsService: UtilsService,
  ) {
    this.window = this.document.defaultView;
    // this._adaptiveThemeUrl$
    //   .pipe(
    //     distinctUntilChanged(),
    //     takeUntil(this._destroy$),
    //     //filter((n) => n !== null),
    //   )
    //   .subscribe((value: string) => this.forceUpdate(value));

    this.themeConfig$
      .pipe(
        takeUntil(this._destroy$),
        filter((n) => {
          return n !== null && n.themeName !== undefined && n.themeName != '';
        }),
      )
      .subscribe((value: CurrentTheme) => {
        //debugger;
        AppSettings.UIAppConfig.currentTheme = value;
        const theme = new CurrentTheme(value);

        this.window['UIAppConfig'] = AppSettings.UIAppConfig;
        this.window['logoUrl'] = theme.LogoUrlPath;
        this.window['heartbeatLogoUrl'] =
          theme.themeId === 0
            ? './assets/images/logo.png'
            : theme.heartbeatLogoUrlPath;

        this.utilsService.setSiteWideThemeFontFamily(
          theme.font || AppSettings.UIAppConfig?.currentTheme?.font,
        );

        const themeColor = theme?.colors
          ? theme.colors
          : this.utilsService.getSkybitzBlueColorValue();
        this.utilsService.setSiteWideThemeColor(themeColor);

        this.currentTheme$.next(theme);
      });
  }

  updateTheme(adaptiveThemeUrl: string | null) {
    this.logger.log(`Updating theme to ${adaptiveThemeUrl}`);
    this._adaptiveThemeUrl$.next(adaptiveThemeUrl);
    this.adaptiveThemeValue = adaptiveThemeUrl ?? '';
  }

  forceUpdate(theme: string) {
    this.getFromApi(theme).subscribe((config: CurrentTheme) => {
      this.themeConfig$.next(config);
    });
  }

  getDominantColorFromImage(themeId: number) {
    // The api resolves the logo url from the theme record itself - passing a url would let the
    // caller choose what the server fetches.
    var endpoint = `appstate/get-dominant-color-from-image?themeId=${themeId}`;
    return this.http.getDirect<IDominantColor>(endpoint);
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  private getFromApi(themeName: string): Observable<CurrentTheme> {
    return this.http
      .getDirect<ICurrentTheme>(
        `/api/v1/appstate/get-theme-by-url/${themeName}`,
      )
      .pipe(
        takeUntil(this._destroy$),
        map((x: ICurrentTheme): CurrentTheme => {
          // The slug we asked for is the slug in play, whatever the theme is called.
          x.themePathSegment = themeName;
          return new CurrentTheme(x);
        }),
      );
  }
}

export interface IDominantColor {
  color: string;
}
